import React, { memo, useState } from 'react';
import './index.less';
import PropTypes from 'prop-types';
import RcResizeObserver from 'rc-resize-observer';
import { UpCircleOutlined, DownCircleOutlined } from '@ant-design/icons';
import { Button, Row, Col, Divider } from 'antd';
import FieldLabel from './components/FieldLabel';

const noop = () => { };

const prefixCls = 'eh-table-query';

// 筛选区域宽度 ≤ 858 px，2列
// 筛选区域宽度 ≤ 1272 px，3列
// 筛选区域宽度 > 1272 px，4列
const DEFAULT_BREAKPOINTS = [
	[858, 2],
	[1272, 3],
	[Infinity, 4],
];

/**
 * 表格查询组件
 * @param {object} props
 */
const TableQuery = props => {
	const { span, queryItems, onReset, onSubmit, breakPoints, hideBorderBottom } = props;

	const [showAll, setShowAll] = useState(false);
	// 不使用的变量，用于resize时触发FieldLabel更新
	const [containerWidth, setContainerWidth] = useState(0);

	const [autoSpan, setAutoSpan] = useState(() => {
		return 24 / breakPoints[0][1];
	});

	/**
	 * 计算并设置autoSpan的值
	 * @param {number} width 容器宽度
	 */
	function setSpan (width) {
		const breakPoint = breakPoints.find(it => it[0] >= width);
		if (breakPoint) {
			setAutoSpan(24 / breakPoint[1]);
		}
	}

	/**
	 * 切换全部显示
	 */
	function onToggle () {
		setShowAll(!showAll);
	}

	/**
	 * 尺寸变化回调
	 */
	function onResize ({ width }) {
		setSpan(width);
		setContainerWidth(width);
	}

	/**
	 * 生成 field item
	 */
	let currentWidth = 0;

	function genItems ({ key, label, node, isDoubleWidth = false }) {
		currentWidth = currentWidth + (span !== void 0 ? span : autoSpan) * (isDoubleWidth ? 2 : 1);
		const hide = !showAll && currentWidth > 24;
		return <Col className={`${prefixCls}-item-wrapper ${hide ? prefixCls + '-hidden' : ''}`} key={key || label} span={(span === void 0 ? autoSpan : span) * (isDoubleWidth ? 2 : 1)}>
			<div className={`${prefixCls}-item-label`}>{typeof label === 'string' ? <FieldLabel>{label}</FieldLabel> : label}</div>
			<div className={`${prefixCls}-item-field`}>{node}</div>
		</Col>;
	}

	// 监听回车键
	function onKeyUp (e) {
		if (e.key === 'Enter') {
			if (typeof props.onPressEnter === 'function') {
				props.onPressEnter(e);
			} else if (typeof props.onSubmit === 'function') {
				props.onSubmit(e);
			}
		}
	}

	// 是否应该隐藏折叠按钮
	let sumSpan = 0;
	queryItems.forEach(item => {
		sumSpan = sumSpan + (span !== void 0 ? span : autoSpan) * (item.isDoubleWidth ? 2 : 1);
	});
	const hideCollapseBtn = sumSpan < 24;

	return queryItems.length === 0
		? null
		: <div className={prefixCls + ' ' + (props.className || '') + (hideBorderBottom ? ' hide-border-bottom' : '')} onKeyUp={onKeyUp}>
			<div className={`${prefixCls}-container`}>
				<div className={`${prefixCls}-items-wrapper`}>
					<RcResizeObserver
						onResize={onResize}
					>
						<Row className={`${prefixCls}-items-wrapper`} type='flex' gutter={16}>
							{queryItems.map(genItems)}
						</Row>
					</RcResizeObserver>
				</div>
				<Divider type='vertical' />
				<div className={`${prefixCls}-btns-wrapper`}>
					<Button onClick={onReset}>重置</Button>
					<Button type='primary' onClick={onSubmit}>查询</Button>
					{!hideCollapseBtn && (showAll
						? <UpCircleOutlined className={`${prefixCls}-collapse-btn ${prefixCls}-collapse-btn-open`} onClick={onToggle} />
						: <DownCircleOutlined className={`${prefixCls}-collapse-btn`} onClick={onToggle} />)}
				</div>
			</div>
		</div>;
};

TableQuery.propTypes = {
	span: PropTypes.number, // ?/24
	breakPoints: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)), // 断点设置
	queryItems: PropTypes.arrayOf(PropTypes.shape({
		label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
		node: PropTypes.element,
		key: PropTypes.string,
		isDoubleWidth: PropTypes.bool
	})),
	onReset: PropTypes.func,
	onSubmit: PropTypes.func,
	hideBorderBottom: PropTypes.bool, // 是否隐藏底部分隔线
	onPressEnter: PropTypes.func, // 按下回车的回调
};

TableQuery.defaultProps = {
	queryItems: [],
	breakPoints: DEFAULT_BREAKPOINTS,
	onReset: noop,
	onSubmit: noop,
	hideBorderBottom: false,
};

export default memo(TableQuery);
